import {
  ref,
  uploadBytes,
} from 'firebase/storage'
import {
  doc,
  getDoc,
  getDocs,
  updateDoc,
  setDoc,
  query,
  where,
  orderBy,
  writeBatch,
  collection,
  getCountFromServer,
} from 'firebase/firestore'
import { getUnix } from '@/libs/date-format'
import { uniqBy, isEmpty } from 'lodash'
import { db } from '@/libs/firebase'
import { RevisionEventType } from '@core/revisions/revisions'
import { constructQuery } from '@core/utils/utils'
import { allowed, FEATURES } from '@/auth'
import store from '@/store'
import i18n from '@/libs/i18n'
import { getTenantContextInstance as tenantCtx } from '@/plugins/tenant'
import router from '@/router'
import { transformForStorage } from '@/libs/content-translator/mutator'
import { stripEmptyValues } from '@/libs/form-translations/normalize'
import { loadFormSchemaFromStorage, uploadFormSchemaToStorage } from '@/views/forms/forms/schema-storage'

export default {
  namespaced: true,
  state: {
    list: [],
    listRelated: [],
    translations: [],
    current: {
      snapshot: {},
      data: {
        schema: '{}',
      },
    },
    errors: {
      FORMS_EMBEDDING: {
        hasError: false,
        errorMessage: '',
      },
    },
    count: null,
    formEngineIntegration: {
      type: process.env.VUE_APP_DEFAULT_FORM_ENGINE,
      icons: process.env.VUE_APP_DEFAULT_FORMIO_ICONS,
      bootstrapVersion: process.env.VUE_APP_DEFAULT_FORMIO_BS_VERSION,
    }
  },
  getters: {
    getCount: state => state.count,
    getErrors: state => state.errors,
    getCurrent: state => state.current,
    getTranslations: state => state.translations,
    getFormEngineIntegration: state => state.formEngineIntegration,
    getAll: state => state.list.map(docRef => ({ ...docRef.data(), id: docRef.id })).map(({ schema, ...item }) => item).filter(el => !el.deletedAt),
    getById: state => id => state.list.map(docRef => ({ snapshot: docRef, data: { ...docRef.data(), id: docRef.id } })).filter(el => !el.data.deletedAt).find(item => item.snapshot.id === id),
    getFilteredList: state => state.list.map(docRef => ({ ...docRef.data(), id: docRef.id })).map(({ schema, ...item }) => item).filter(el => !el.deletedAt),
    getRelated: state => state.listRelated.map(docRef => ({ ...docRef.data(), id: docRef.id })).map(({ schema, ...item }) => item).filter(el => !el.deletedAt),
  },
  mutations: {
    UPDATE_LIST(state, payload) {
      const { docs, pagination } = payload

      state.list = (!pagination) ? docs : uniqBy([...state.list, ...docs], 'id')
    },
    UPDATE_LIST_RELATED(state, payload) {
      state.listRelated = payload
    },
    UPDATE_FILTERED_LIST(state, payload) {
      state.list = payload
    },
    UPDATE_TRANSLATIONS(state, payload) {
      state.translations = payload
    },
    SET_CURRENT(state, payload) {
      state.current.snapshot = payload
      state.current.data = ({ ...payload.data(), id: payload.id })
    },
    SET_CURRENT_TAGS(state, tags) {
      state.current.data.tags = tags
    },
    SET_ERROR(state, payload) {
      state.errors[payload.target] = payload
    },
    SET_COUNT(state, payload) {
      state.count = payload
    },
    SET_FORM_ENGINE_INTEGRATION(state, payload) {
      state.formEngineIntegration = payload
    },
  },
  actions: {
    /**
     * Fetches all
     *
     * @param {Object}
     * @param {Object} payload
     *
     * @returns {Promise}
     */
    fetchAll({ commit }, payload = {}) {
      return new Promise((resolve, reject) => {
        const { forms } = tenantCtx()

        const { queryOptions, pagination = false } = payload

        const q = constructQuery(forms, 'description', {
          ...queryOptions,
          where: [where('deletedAt', '==', null)],
        })

        getDocs(q)
          .then(querySnapshot => {
            commit('UPDATE_LIST', { docs: querySnapshot.docs, pagination })
            resolve(querySnapshot.docs)
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong retrieving the Forms'),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * Fetches document count
     *
     * @param {Object} context - Vuex context
     *
     * @returns {Promise<number>} - Count of matching documents
     */
    fetchCount({ commit }) {
      return new Promise(async (resolve, reject) => {
        try {
          const { forms } = tenantCtx()

          const q = query(
            forms,
            where('deletedAt', '==', null),
          )

          const snapshot = await getCountFromServer(q)
          const { count } = snapshot.data()

          commit('SET_COUNT', count)
          resolve(count)
        } catch (error) {
          store.dispatch('notify', {
            body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('forms') }),
            variant: 'danger',
          })
          reject(error)
        }
      })
    },

    /**
     * Fetches on search query
     *
     * @param {Object}
     * @param {Object} queryOptions
     *
     * @returns {Promise}
     */
    fetchFiltered({ commit }, queryOptions) {
      return new Promise((resolve, reject) => {
        const { forms } = tenantCtx()

        const q = constructQuery(forms, 'createdAt', {
          ...queryOptions,
          where: [...queryOptions.where, where('deletedAt', '==', null)],
        })

        getDocs(q)
          .then(querySnapshot => {
            commit('UPDATE_FILTERED_LIST', querySnapshot.docs)
            resolve(querySnapshot.docs)
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong retrieving the forms'),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * Fetches a document by the given ID
     *
     * @param {Object} id The page ID
     *
     * @returns {Promise} Retrieves all fields in the document as an Object. Returns undefined if the document doesn't exist.
     */
    fetchById({ }, id) {
      return new Promise(async (resolve, reject) => {
        // Validate that id is not empty, null, or undefined
        if (!id || id === '' || id === 'undefined' || id === 'null') {
          reject(new Error('Form ID cannot be empty'))
          return
        }

        const { forms } = tenantCtx()
        const docRef = doc(forms, id)

        try {
          const docSnapshot = await getDoc(docRef)

          if (!docSnapshot.exists()) {
            reject(new Error('404'))
            return
          }

          if (docSnapshot.data().deletedAt) {
            reject(new Error('404'))
            return
          }

          // Load schema from Cloud Storage
          const schema = await loadFormSchemaFromStorage(id) || {}

          // Merge schema into document data
          const docData = docSnapshot.data()
          const docWithSchema = {
            ...docData,
            schema: JSON.stringify(schema),
          }

          // Create a wrapper that preserves all Firestore snapshot properties
          // Use Object.create to preserve prototype chain and non-enumerable properties
          const mergedSnapshot = Object.create(Object.getPrototypeOf(docSnapshot))
          
          // Copy all properties from the original snapshot
          Object.assign(mergedSnapshot, docSnapshot)
          
          // Override the data method to include schema
          mergedSnapshot.data = () => docWithSchema

          resolve(mergedSnapshot)
        } catch (error) {
          store.dispatch('notify', {
            body: i18n.t('Something went wrong retrieving the {title}', { title: 'Form' }),
            variant: 'danger',
          })

          reject(error)
        }
      })
    },

    /**
     * Fetches a document by the current item ID
     *
     * @param {string} id The current page ID
     */
    fetchCurrent({ dispatch, commit }, id) {
      return new Promise(async (resolve, reject) => {
        try {
          const res = await dispatch('fetchById', id)

          // Load schema from Cloud Storage
          const schema = await loadFormSchemaFromStorage(id) || {}

          // Create snapshot with schema merged in, preserving all Firestore properties
          // Use Object.create to preserve prototype chain
          const snapshotWithSchema = Object.create(Object.getPrototypeOf(res))
          
          // Copy all properties from the original snapshot
          Object.assign(snapshotWithSchema, res)
          
          // Override the data method to include schema
          snapshotWithSchema.data = () => ({
            ...res.data(),
            schema: JSON.stringify(schema),
          })

          commit('SET_CURRENT', snapshotWithSchema)

          // Set the form engine integration
          const { formEngine } = res.data()

          // Since we're preparing to allow clients to choose their own form engine,
          // we need to fetch the related integration for the current form
          // to prepare the form and form builder correctly.
          if (formEngine) {
            store.dispatch('integrations/fetchByType', formEngine)
              .then(integration => {
                if (integration) {
                  commit('SET_FORM_ENGINE_INTEGRATION', integration.data())
                }
              })
              .catch(error => {
                console.debug(error)

                reject(error)
              })
          }

          resolve(snapshotWithSchema)
        } catch (error) {
          reject(error)
        }
      })
    },

    /**
     * Creates a document by the given payload
     *
     * @param {Object} payload
     *
     * @returns {Promise}
     */
    create({ }, payload) {
      return new Promise((resolve, reject) => {
        if (!allowed(FEATURES.FORMS_QUOTA)) {
          reject()

          store.dispatch('notify', {
            body: i18n.t('You have reached your {title} limit', { title: i18n.t('forms') }),
            variant: 'danger',
          })

          return
        }

        const { forms } = tenantCtx()

        // This is required if we want to filter on `deletedAt == null`, as per Firestore query limitations
        // eslint-disable-next-line no-param-reassign
        payload.deletedAt = null

        const docRef = doc(forms)

        setDoc(docRef, payload)
          .then(async () => {
            // Create an empty schema file in storage to prevent 404 errors when editing
            try {
              const emptySchema = {}
              await uploadFormSchemaToStorage(docRef.id, emptySchema)
            } catch (storageError) {
              // Log but don't fail form creation if storage upload fails
              console.error('Error creating empty schema file in storage:', storageError)
            }

            store.dispatch('revisions/create', {
              event: RevisionEventType.CREATE_FORMS,
              id: docRef.id,
              newValue: payload,
              previousValue: {},
            })
            store.dispatch('notify', { title: i18n.t('Great!'), body: i18n.t('A new {title} has been created 🚀', { title: i18n.t('form') }) })

            resolve(docRef)
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong creating the Form'),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * Updates a document by the given payload
     *
     * @param {Object} obj
     * @param {Object} obj.dispatch
     * @param {Object} obj.state
     * @param {Object} payload
     *
     * @returns {Promise}
     */
    updateCurrent({ dispatch, state }, payload) {
      return new Promise(async (resolve, reject) => {
        const { formsStorage } = tenantCtx()

        const { previousValue, translations, data, schema } = payload

        try {
          const {
            snapshot: { id },
            data: currentData,
          } = state.current

          // Remove schema and tags from data object before updating Firestore (tags live in tagsResources)
          const { schema: _, tags: __, ...dataWithoutSchema } = data

          // Prepare Firestore update data
          const firestoreUpdateData = { ...dataWithoutSchema }

          // Upload schema to Cloud Storage if provided
          if (schema) {
            try {
              const uploadResult = await uploadFormSchemaToStorage(id, schema)
              const { metadata } = uploadResult

              // Store storageVersion metadata in Firestore
              firestoreUpdateData.storageVersion = {
                md5Hash: metadata.md5Hash,
                generation: metadata.generation,
              }
            } catch (error) {
              console.error('Error uploading schema to Cloud Storage:', error)
              // Continue with Firestore update even if Cloud Storage upload fails
            }
          }

          // Update Firestore document (without schema field)
          await updateDoc(state.current.snapshot.ref, firestoreUpdateData)

          if (!isEmpty(translations)) {
            await dispatch('updateTranslations', {
              id,
              translations,
            })
          }

          if (!isEmpty(data?.embeddedSettings)) {
            const embeddedSettingsRef = ref(formsStorage, `${id}.embedded-settings.json`)
            const embeddedSettingsBlob = new Blob([JSON.stringify(data.embeddedSettings)], { type: 'application/json' })

            uploadBytes(embeddedSettingsRef, embeddedSettingsBlob)
          }

          // Include schema in revision for historical tracking
          const revisionNewValue = {
            ...currentData,
            ...(schema ? { schema: JSON.stringify(schema) } : {}),
          }

          store.dispatch('revisions/create', {
            event: RevisionEventType.UPDATE_FORMS,
            id,
            newValue: revisionNewValue,
            previousValue,
          })

          store.dispatch('notify', { body: i18n.t('{title} has been updated', { title: i18n.t('Form') }) })
          resolve(true)
        } catch (error) {
          store.dispatch('notify', {
            body: i18n.t('Something went wrong updating the form!'),
            variant: 'danger',
          })

          reject(error)
        }
      })
    },

    /**
     * Soft deletes a document
     *
     * @param {Object} payload
     *
     * @returns {Promise}
     */
    softDelete({ }, payload) {
      return new Promise((resolve, reject) => {
        const { forms } = tenantCtx()
        const docRef = doc(forms, payload.id)

        updateDoc(docRef, {
          deletedAt: payload.deletedAt,
        })
          .then(() => {
            store.dispatch('revisions/create', {
              event: RevisionEventType.DELETE_FORMS,
              id: payload.id,
              newValue: {},
              previousValue: payload,
            })
            resolve(true)
          })
          .catch(error => {
            reject(error)
          })
      })
    },

    /**
     * Duplicates a document
     *
     * @param {Object}
     * @param {string} id The id of the document to duplicate
     *
     * @returns {Promise}
     */
    duplicate({ }, id) {
      return new Promise(async (resolve, reject) => {
        if (!allowed(FEATURES.FORMS_QUOTA)) {
          reject()

          store.dispatch('notify', {
            body: i18n.t('You have reached your {title} limit', { title: i18n.t('forms') }),
            variant: 'danger',
          })

          return
        }

        try {
          const timestamp = getUnix()
          const { forms } = tenantCtx()
          const docRef = doc(forms, id)
          const docSnapshot = await getDoc(docRef)
          const docSnapshotData = docSnapshot.data()
          const newDocRef = doc(forms)

          // Legacy schema field on Firestore documents is no longer used (schema lives in Cloud Storage)
          const { schema: _, ...docDataWithoutSchema } = docSnapshotData

          const data = {
            ...docDataWithoutSchema,
            id: newDocRef.id,
            description: `${docSnapshotData.description} ${timestamp} - ${i18n.t('COPY')}`,
            createdAt: timestamp,
            deletedAt: null,
            updatedAt: null,
          }

          // Try to copy the schema JSON in Cloud Storage to the new form id
          try {
            const originalSchema = await loadFormSchemaFromStorage(id)

            if (originalSchema) {
              const uploadResult = await uploadFormSchemaToStorage(newDocRef.id, originalSchema)
              const { metadata } = uploadResult || {}

              if (metadata) {
                data.storageVersion = {
                  md5Hash: metadata.md5Hash,
                  generation: metadata.generation,
                }
              }
            }
          } catch (schemaError) {
            // Log but do not fail the duplication if schema copy fails
            console.error('Error duplicating form schema in storage:', schemaError)
          }

          await setDoc(newDocRef, data)

          const translationsCollection = collection(forms, id, 'translations')
          const translations = await getDocs(translationsCollection)

          if (!translations.empty) {
            const newTranslationsCollection = collection(forms, newDocRef.id, 'translations')
            const batch = writeBatch(db)

            // eslint-disable-next-line
            for (const document of translations.docs) {
              const translationsDocRef = doc(newTranslationsCollection, document.id)

              batch.set(translationsDocRef, document.data())
            }

            batch.commit().then(() => resolve(true))
          }

          await store.dispatch('revisions/create', {
            event: RevisionEventType.DUPLICATE_FORMS,
            id: newDocRef.id,
            newValue: data,
            previousValue: {},
          })

          await router.push({
            name: 'forms-edit',
            params: { id: newDocRef.id },
          })

          await store.dispatch('notify', {
            title: i18n.t('Great!'),
            body: i18n.t('A new {title} has been duplicated 🚀', { title: i18n.t('form') }),
          })

          resolve(newDocRef)
        } catch (error) {
          store.dispatch('notify', {
            body: i18n.t('Something went wrong duplicating the {title}', { title: i18n.t('form') }),
            variant: 'danger',
          })

          reject(error)
        }
      })
    },

    /**
     * Fetch all forms which correspond to having a specific Form Configuration Id
     *
     * @param {Object}
     * @param {string} id Id of the form configuration
     *
     * @returns {Promise}
     */
    fetchAllByConfigurationId({ commit }, id) {
      return new Promise((resolve, reject) => {
        const { forms } = tenantCtx()

        const q = query(forms, where('formConfigurationId', '==', id), orderBy('createdAt', 'desc'))

        getDocs(q)
          .then(querySnapshot => {
            commit('UPDATE_LIST_RELATED', querySnapshot.docs)
            resolve(querySnapshot.docs)
          })
          .catch(error => reject(error))
      })
    },

    /**
     * Fetch all the translations by the given id
     *
     * @param {Object} obj
     * @param {Object} obj.commit
     * @param {string} id
     *
     * @returns {Promise<{[language: string]: { [key: string]: string }}>}
     */
    fetchAllTranslations({ commit }, id) {
      return new Promise((resolve, reject) => {
        const { forms } = tenantCtx()

        getDocs(collection(forms, id, 'translations')).then(querySnapshot => {
          const textContentsPerLanguage = querySnapshot.docs
            .sort((a, b) => a.id.localeCompare(b.id))
            .reduce((acc, curr) => {
              acc[curr.id] = curr.data()

              return acc
            }, {})

          commit('UPDATE_TRANSLATIONS', textContentsPerLanguage)
          resolve(textContentsPerLanguage)
        }).catch(error => {
          store.dispatch('notify', {
            body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('translations') }),
            variant: 'danger',
          })

          reject(error)
        })
      })
    },

    /**
     * Update the translations
     *
     * @param {Object} payload
     *
     * @returns {Promise<Boolean>}
     */
    async updateTranslations({ state }, payload) {
      const { forms, formsStorage } = tenantCtx()

      try {
        const { id } = payload
        const translations = transformForStorage(payload.translations || {})

        const deletedLanguages = Object.keys(state.translations || {}).filter(language => !translations[language])

        if (deletedLanguages.length === 0 && Object.keys(translations).length === 0) {
          return true
        }

        const batch = writeBatch(db)
        const translationsCollection = collection(forms, id, 'translations')

        // Whole-document `set` so keys containing `.` or markup can never be interpreted as a field path.
        Object.keys(translations).forEach(language => {
          batch.set(doc(translationsCollection, language), translations[language])
        })

        deletedLanguages.forEach(language => {
          batch.delete(doc(translationsCollection, language))
        })

        await batch.commit()

        // Empty values are kept in Firestore (tracked as "missing") but must not reach the runtime bundle:
        // i18next would render the empty string instead of falling back to the source text.
        const translationsRef = ref(formsStorage, `/translations/${id}.json`)
        const translationBlob = new Blob([JSON.stringify(stripEmptyValues(translations))], { type: 'application/json' })

        await uploadBytes(translationsRef, translationBlob)

        return true
      } catch (error) {
        console.error('Error updating form translations:', error)

        store.dispatch('notify', {
          body: i18n.t('Something went wrong updating the {title}', { title: i18n.t('translations') }),
          variant: 'danger',
        })

        throw error
      }
    },
  },
}
